/** Bounded adaptive backoff for GitHub installation refresh after a new-tab grant. */
export const GITHUB_WATCH_WINDOW_MS = 2 * 60 * 1000;
export const GITHUB_WATCH_DELAYS_MS = [0, 2000, 4000, 8000, 15000, 30000];

export function nextGithubWatchDelay(attempt) {
  const index = Math.max(0, Math.min(GITHUB_WATCH_DELAYS_MS.length - 1, Number(attempt) || 0));
  return GITHUB_WATCH_DELAYS_MS[index];
}

export function createGithubRefreshWatch({
  now = () => Date.now(),
  schedule = (fn, ms) => window.setTimeout(fn, ms),
  clear = (id) => window.clearTimeout(id),
  refresh,
  isActive = () => true,
  onIdle,
} = {}) {
  let timer = null;
  let attempt = 0;
  let inFlight = false;
  let deadline = 0;
  let stopped = true;

  function clearTimer() {
    if (timer != null) {
      clear(timer);
      timer = null;
    }
  }

  function stop(reason = "stopped") {
    stopped = true;
    deadline = 0;
    attempt = 0;
    clearTimer();
    onIdle?.(reason);
  }

  function arm() {
    clearTimer();
    if (stopped || !isActive()) return;
    if (now() >= deadline) {
      stop("deadline");
      return;
    }
    const delay = nextGithubWatchDelay(attempt);
    timer = schedule(() => {
      const id = timer;
      timer = null;
      if (id != null) clear(id);
      void tick();
    }, delay);
  }

  async function tick() {
    if (stopped || !isActive()) return;
    if (now() >= deadline) {
      stop("deadline");
      return;
    }
    if (inFlight) {
      arm();
      return;
    }
    inFlight = true;
    try {
      const result = await refresh();
      if (result?.done) {
        stop("complete");
        return;
      }
    } finally {
      inFlight = false;
    }
    if (stopped || !isActive()) return;
    attempt += 1;
    arm();
  }

  return {
    start(windowMs = GITHUB_WATCH_WINDOW_MS) {
      stopped = false;
      attempt = 0;
      deadline = now() + windowMs;
      arm();
    },
    kick() {
      if (stopped || !isActive()) return;
      attempt = 0;
      clearTimer();
      void tick();
    },
    stop,
    get active() {
      return !stopped && now() < deadline;
    },
    get inFlight() {
      return inFlight;
    },
  };
}
